/**
 * Düğüne özel baskı şablonları — masa kartı A6, karşılama panosu 50×70,
 * el kartı 85×55. Tasarım landing'deki örnek PDF'lerle aynı
 * (scripts/print-templates/templates.mjs); burada düğünün KENDİ adları,
 * tarihi, QR'ı (buildScanUrl) ve masa numaralarıyla üretilir.
 *
 * Her şablon tam bir HTML belgesi döner: `@page` gerçek ölçüde → tarayıcının
 * "PDF olarak kaydet"i matbaaya hazır, vektör metin + vektör QR verir.
 * Tüm kullanıcı girdisi `esc()`'ten geçer (ad/tarih/masa → HTML enjeksiyonu yok).
 */

export type TemplateId = "table-card" | "welcome-sign" | "hand-card";

/** Şablon metinleri — DÜĞÜNÜN dilinde (yönetici arayüzünün dili değil). */
export interface PrintCopy {
  shareEyebrow: string;
  step1: string;
  step2: string;
  step3: string;
  table: string;
  welcome: string;
  headlineA: string;
  headlineB: string;
  captionStrong: string;
  caption: string;
  noApp: string;
  handEyebrow: string;
  handLine1: string;
  handLine2: string;
  handSteps: string;
}

export interface PrintCtx {
  nameA: string;
  nameB: string;
  /** "23 · 08 · 2026" */
  dateDots: string;
  /** Vektör QR (qrcode → svg). Güvenilir kaynaktan — kaçışlanmaz. */
  qrSvg: string;
  /** Masa kartında sayfa başına bir masa ("1", "2", "VIP"…); boşsa tek sayfa, masa satırı yok. */
  tables: string[];
  /** Kartın altındaki alan adı, ör. "wedding.mertbal.com". */
  domain: string;
  copy: PrintCopy;
}

export interface PrintDoc {
  html: string;
  widthMm: number;
  heightMm: number;
  pages: number;
}

export function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const FONTS =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Playfair+Display:ital,wght@0,400;0,500;1,400;1,500&display=swap";

const BASE_CSS = `
  :root {
    --paper: #f6f5f3; --paper-2: #eceae7; --line: #dddad6;
    --ink: #1a1816; --soft: #6b6460;
    --night: #1b1817; --night-ink: #f0eeec; --night-soft: #b4aba7;
    --accent: #8d6b5e; --accent-soft: #c4ada4;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Inter, system-ui, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; -webkit-font-smoothing: antialiased; }
  .serif { font-family: "Playfair Display", Georgia, serif; font-weight: 400; }
  .it { font-style: italic; }
  .eyebrow { text-transform: uppercase; font-weight: 500; }
  .center { text-align: center; }
  .qr svg { display: block; width: 100%; height: 100%; }
  .brackets { position: relative; }
  .brackets i { position: absolute; width: 22%; height: 22%; border-color: var(--accent); border-style: solid; border-width: 0; }
  .brackets i:nth-child(1) { left: 0; top: 0; border-left-width: var(--bw); border-top-width: var(--bw); border-top-left-radius: var(--br); }
  .brackets i:nth-child(2) { right: 0; top: 0; border-right-width: var(--bw); border-top-width: var(--bw); border-top-right-radius: var(--br); }
  .brackets i:nth-child(3) { left: 0; bottom: 0; border-left-width: var(--bw); border-bottom-width: var(--bw); border-bottom-left-radius: var(--br); }
  .brackets i:nth-child(4) { right: 0; bottom: 0; border-right-width: var(--bw); border-bottom-width: var(--bw); border-bottom-right-radius: var(--br); }
  .page { position: relative; overflow: hidden; break-after: page; }
  .page:last-child { break-after: auto; }
`;

function doc(widthMm: number, heightMm: number, css: string, pages: string[], lang: string): PrintDoc {
  const html = `<!doctype html>
<html lang="${esc(lang)}">
<head>
<meta charset="utf-8" />
<link rel="stylesheet" href="${FONTS}" />
<style>
  @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
  html, body { width: ${widthMm}mm; }
  .page { width: ${widthMm}mm; height: ${heightMm}mm; }
  ${BASE_CSS}
  ${css}
</style>
</head>
<body>${pages.map((p) => `<div class="page">${p}</div>`).join("")}</body>
</html>`;
  return { html, widthMm, heightMm, pages: pages.length };
}

const brackets = `<i></i><i></i><i></i><i></i>`;

function tableCard(ctx: PrintCtx, lang: string): PrintDoc {
  const c = ctx.copy;
  const css = `
    .card { position: relative; width: 100%; height: 100%; background: var(--night); color: var(--night-ink); }
    .frame { position: absolute; inset: 6mm; border: 0.3mm solid var(--accent); }
    .frame::after { content: ""; position: absolute; inset: 1.2mm; border: 0.15mm solid rgba(141,107,94,.55); }
    .inner { position: absolute; inset: 6mm; display: flex; flex-direction: column; align-items: center; padding: 11mm 9mm 8mm; }
    .eyebrow { font-size: 2.1mm; letter-spacing: .34em; color: var(--night-soft); }
    .names { margin-top: 5mm; font-size: 11.5mm; line-height: 1; letter-spacing: -.02em; }
    .names .amp { color: var(--accent-soft); }
    .date { margin-top: 3.2mm; font-size: 2.4mm; letter-spacing: .32em; color: var(--accent-soft); }
    .qrwrap { margin-top: 7mm; width: 52mm; height: 52mm; padding: 2.6mm; --bw: .5mm; --br: 2.4mm; }
    .qr { width: 100%; height: 100%; background: #fff; border-radius: 1.6mm; padding: 2.6mm; }
    .steps { margin-top: 6.5mm; display: flex; gap: 2.6mm; font-size: 2.15mm; color: var(--night-soft); }
    .steps b { color: var(--accent-soft); font-weight: 500; margin-right: .8mm; }
    .table { margin-top: auto; font-size: 6.5mm; }
    .brand { margin-top: 1.6mm; font-size: 1.9mm; letter-spacing: .12em; color: var(--night-soft); opacity: .7; }
  `;
  const tables = ctx.tables.length ? ctx.tables : [""];
  const pages = tables.map(
    (table) => `
    <div class="card">
      <div class="frame"></div>
      <div class="inner">
        <p class="eyebrow center">${esc(c.shareEyebrow)}</p>
        <p class="names serif it center">${esc(ctx.nameA)} <span class="amp">&amp;</span> ${esc(ctx.nameB)}</p>
        <p class="date center">${esc(ctx.dateDots)}</p>
        <div class="qrwrap brackets">${brackets}<div class="qr">${ctx.qrSvg}</div></div>
        <div class="steps"><span><b>1</b>${esc(c.step1)}</span><span><b>2</b>${esc(c.step2)}</span><span><b>3</b>${esc(c.step3)}</span></div>
        ${table ? `<p class="table serif it center">${esc(c.table)} ${esc(table)}</p>` : `<p class="table"></p>`}
        <p class="brand center">${esc(ctx.domain)}</p>
      </div>
    </div>`,
  );
  return doc(105, 148, css, pages, lang);
}

function welcomeSign(ctx: PrintCtx, lang: string): PrintDoc {
  const c = ctx.copy;
  const css = `
    .sign { position: relative; width: 100%; height: 100%; background: var(--paper); color: var(--ink); overflow: hidden; }
    .glow { position: absolute; left: 50%; top: 52%; width: 520mm; height: 520mm; transform: translate(-50%,-50%); border-radius: 50%;
            background: radial-gradient(closest-side, rgba(141,107,94,.14), rgba(141,107,94,0) 70%); }
    .arch { position: absolute; inset: 34mm 34mm 34mm; border: 1.3mm solid var(--accent); border-radius: 216mm 216mm 6mm 6mm; }
    .arch::after { content: ""; position: absolute; inset: 6mm; border: .5mm solid rgba(141,107,94,.45); border-radius: 210mm 210mm 3mm 3mm; }
    .inner { position: absolute; inset: 34mm; display: flex; flex-direction: column; align-items: center; padding: 92mm 40mm 34mm; }
    .eyebrow { font-size: 11mm; letter-spacing: .4em; color: var(--soft); }
    .names { margin-top: 14mm; font-size: 50mm; line-height: 1; letter-spacing: -.025em; white-space: nowrap; }
    .names .amp { color: var(--accent); }
    .date { margin-top: 12mm; font-size: 12mm; letter-spacing: .36em; color: var(--accent); }
    .rule { margin-top: 18mm; width: 70mm; height: .6mm; background: var(--accent); opacity: .5; }
    .headline { margin-top: 18mm; font-size: 26mm; line-height: 1.12; letter-spacing: -.015em; }
    .headline em { color: var(--accent); }
    .qrwrap { margin-top: 22mm; width: 184mm; height: 184mm; padding: 9mm; --bw: 1.6mm; --br: 9mm; }
    .qr { width: 100%; height: 100%; background: #fff; border-radius: 5mm; padding: 9mm; box-shadow: 0 6mm 18mm rgba(26,24,22,.12); }
    .caption { margin-top: 18mm; font-size: 12mm; line-height: 1.5; color: var(--soft); }
    .caption b { color: var(--ink); font-weight: 500; }
    .foot { margin-top: 12mm; font-size: 7.5mm; letter-spacing: .3em; color: var(--soft); opacity: .8; }
  `;
  const page = `
    <div class="sign">
      <div class="glow"></div>
      <div class="arch"></div>
      <div class="inner">
        <p class="eyebrow center">${esc(c.welcome)}</p>
        <p class="names serif it center">${esc(ctx.nameA)} <span class="amp">&amp;</span> ${esc(ctx.nameB)}</p>
        <p class="date center">${esc(ctx.dateDots)}</p>
        <div class="rule"></div>
        <p class="headline serif center">${esc(c.headlineA)}<br /><em class="it">${esc(c.headlineB)}</em></p>
        <div class="qrwrap brackets">${brackets}<div class="qr">${ctx.qrSvg}</div></div>
        <p class="caption center"><b>${esc(c.captionStrong)}</b> ${esc(c.caption)}</p>
        <p class="foot eyebrow center">${esc(c.noApp)}</p>
      </div>
    </div>`;
  return doc(500, 700, css, [page], lang);
}

function handCard(ctx: PrintCtx, lang: string): PrintDoc {
  const c = ctx.copy;
  const css = `
    .hc { position: relative; width: 100%; height: 100%; background: var(--accent); color: #fff; display: flex; align-items: center; gap: 5mm; padding: 0 6mm; overflow: hidden; }
    .hc::before { content: ""; position: absolute; right: -14mm; top: -14mm; width: 44mm; height: 44mm; border-radius: 50%; border: .3mm solid rgba(255,255,255,.28); }
    .hc::after { content: ""; position: absolute; right: -6mm; top: -6mm; width: 28mm; height: 28mm; border-radius: 50%; border: .2mm solid rgba(255,255,255,.2); }
    .qr { flex: none; width: 33mm; height: 33mm; background: #fff; border-radius: 1.6mm; padding: 2.2mm; }
    .txt { position: relative; }
    .eyebrow { font-size: 1.55mm; letter-spacing: .32em; color: rgba(255,255,255,.75); }
    .line { margin-top: 1.6mm; font-size: 5.2mm; line-height: 1.08; letter-spacing: -.01em; }
    .names { margin-top: 3mm; font-size: 2.6mm; color: rgba(255,255,255,.9); }
    .steps { margin-top: 3.4mm; font-size: 1.75mm; letter-spacing: .22em; color: rgba(255,255,255,.75); }
  `;
  const page = `
    <div class="hc">
      <div class="qr">${ctx.qrSvg}</div>
      <div class="txt">
        <p class="eyebrow">${esc(c.handEyebrow)}</p>
        <p class="line serif it">${esc(c.handLine1)}<br />${esc(c.handLine2)}</p>
        <p class="names serif">${esc(ctx.nameA)} &amp; ${esc(ctx.nameB)} · ${esc(ctx.dateDots)}</p>
        <p class="steps eyebrow">${esc(c.handSteps)}</p>
      </div>
    </div>`;
  return doc(85, 55, css, [page], lang);
}

export const PRINT_TEMPLATES: { id: TemplateId; build: (ctx: PrintCtx, lang: string) => PrintDoc }[] = [
  { id: "table-card", build: tableCard },
  { id: "welcome-sign", build: welcomeSign },
  { id: "hand-card", build: handCard },
];

export const MAX_TABLE_PAGES = 150;

/**
 * "1-12, VIP, 15" → ["1",…,"12","VIP","15"]. Aralık en fazla
 * `MAX_TABLE_PAGES`; geçersiz parça yok sayılır, tekrarlar atılır.
 */
export function parseTables(input: string): string[] {
  const out: string[] = [];
  for (const raw of input.split(",")) {
    const part = raw.trim();
    if (!part) continue;
    const range = /^(\d{1,4})\s*[-–]\s*(\d{1,4})$/.exec(part);
    if (range) {
      const [a, b] = [Number(range[1]), Number(range[2])];
      const [lo, hi] = a <= b ? [a, b] : [b, a];
      for (let n = lo; n <= hi && out.length < MAX_TABLE_PAGES; n++) out.push(String(n));
    } else {
      out.push(part.slice(0, 20));
    }
    if (out.length >= MAX_TABLE_PAGES) break;
  }
  return [...new Set(out)].slice(0, MAX_TABLE_PAGES);
}

/**
 * Düğün başlığından çift adları: "Deniz & Arda", "Ayşe ve Can",
 * "Anna and Tom" → ["Deniz", "Arda"]. Ayrılamazsa başlık + boş.
 */
export function splitCouple(title: string | null | undefined): [string, string] {
  if (!title) return ["", ""];
  const parts = title.split(/\s*(?:&|\+|\s(?:ve|and|et|y)\s)\s*/i).map((s) => s.trim()).filter(Boolean);
  return parts.length >= 2 ? [parts[0], parts.slice(1).join(" ")] : [title.trim(), ""];
}

/** ISO tarih → "23 · 08 · 2026" (yerel saat; düğün günü kaymasın). */
export function dateDots(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())} · ${pad(d.getMonth() + 1)} · ${d.getFullYear()}`;
}
